import {useEffect, useState} from 'react';

/**
 * iOS WKWebView/Safari에서 가상 키보드가 차지하는 높이(px)를 반환한다.
 * - 키보드가 내려가 있으면 0
 * - 키보드가 올라와 있으면 키보드가 가리는 픽셀 높이
 *
 * 안드로이드/데스크탑에서는 항상 0을 반환하므로 기존 동작에 영향 없음.
 *
 * 사용 예 (키보드 올라오면 푸터 숨김):
 *   const keyboardHeight = useIosKeyboardOffset();
 *   <div
 *     className="bottom-btn"
 *     style={keyboardHeight > 0 ? {display: 'none'} : undefined}
 *   />
 */
const isIOS = (): boolean => {
    if (typeof navigator === 'undefined' || typeof window === 'undefined') return false;
    const ua = navigator.userAgent;
    // iPadOS 13+ 는 navigator.userAgent 에 "Macintosh" 가 찍히지만 touch 가 동작하므로 같이 처리
    const isIPadOS = ua.includes('Macintosh') && 'ontouchend' in document;
    return /iPad|iPhone|iPod/.test(ua) || isIPadOS;
};

export function useIosKeyboardOffset(): number {
    const [keyboardHeight, setKeyboardHeight] = useState<number>(0);

    useEffect(() => {
        if (!isIOS()) return;
        if (typeof window === 'undefined' || !window.visualViewport) return;

        const vv = window.visualViewport;

        const handleResize = () => {
            // 키보드가 차지한 영역 = layout viewport 높이 - visual viewport 높이 - visual viewport 의 offsetTop
            const offset = window.innerHeight - vv.height - vv.offsetTop;
            setKeyboardHeight(offset > 0 ? offset : 0);
        };

        handleResize();
        vv.addEventListener('resize', handleResize);
        vv.addEventListener('scroll', handleResize);

        return () => {
            vv.removeEventListener('resize', handleResize);
            vv.removeEventListener('scroll', handleResize);
        };
    }, []);

    return keyboardHeight;
}

export default useIosKeyboardOffset;
